import { useState, useEffect, useMemo } from "react";
import { laboratoryService } from "../../services/laboratory.services";
import { helpFunc } from "../../helpers/helpFunction";
import { mockSamplings } from "./mockData";

// Konfigurácia pre mock dáta - zmeňte na false pre vypnutie
const USE_MOCK_DATA = false;

export const useComplementResults = (patient, care, checkin) => {
  const [samplings, setSamplings] = useState([]);
  const [selectedSampling, setSelectedSampling] = useState(null);
  const [filterOptions, setFilterOptions] = useState("all");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [patientData, setPatientData] = useState(null);
  const [table, setTable] = useState({ loading: true, data: false });
  const [patientRiskLevel, setPatientRiskLevel] = useState("none");

  // Načítání dat pacienta - skopírované z PharmacologicalPlanLogic
  const getPatientData = async (patient, care) => {
    if (patient) {
      try {
        setTable({ loading: true, data: false });
        let data = await helpFunc.pPatientData(patient);
        data = {
          ...data,
          main_diagnosis: care?.main_diagnosis?.name || "Žádná",
          last_dekurz:
            helpFunc.formatDate(care?.last_dekurz?.made_at) || "Žádné datum",
          doctor: helpFunc.pDoctorName(care?.last_dekurz?.doctor),
          clinic: care?.clinic?.abbreviation || "Chybí data o klinice",
          department:
            patient.current_care_last_dekurz_department_description ||
            "Chybí data o oddělení",
          first_visit: helpFunc.formatDate(patient.current_care.started_at),
          ambulant_doctor: care?.ambulant_doctor
            ? `${care?.ambulant_doctor.f_title} ${care?.ambulant_doctor.name} ${care?.ambulant_doctor.l_title}`
            : "Žádné informace",
          external_doctor: care?.doctor || "Žádné informace",
        };
        setTable({ loading: false, data: [data] });
        setPatientData(patient);
      } catch (e) {
        console.error("Chyba při načítání informací o pacientovi:", e);
        setTable({ loading: false, data: null });
      }
    } else {
      setTable({ loading: false, data: null });
    }
  };

  // Načítání dat pacienta
  useEffect(() => {
    if (patient && care) {
      getPatientData(patient, care);

      // Nastavenie risk level pre ColorStripe
      const riskLevel = getPatientRiskLevel(checkin);
      setPatientRiskLevel(riskLevel);
    }
  }, [patient, care, checkin]);

  // Funkcia pre získanie risk level - skopírované z PharmacologicalPlanLogic
  const getPatientRiskLevel = (checkin = {}) => {
    switch (checkin?.risk_level) {
      case "1":
        return "blue";
      case "2":
        return "orange";
      case "3":
        return "red";
      case "none":
        return "none";
      default:
        return "none";
    }
  };

  // Vytvoření stĺpcov pre tabuľku - skopírované z PharmacologicalPlanLogic
  const columns = useMemo(
    () => [
      {
        Header: "Informace o pacientovi",
        columns: [
          {
            Header: "Příjmení a jméno pacienta",
            accessor: "name",
          },
          {
            Header: "Datum narození",
            accessor: "birth_date",
          },
          {
            Header: "Diagnóza",
            accessor: "main_diagnosis",
          },
          {
            Header: "Hmotnost",
            accessor: "weight",
          },
          {
            Header: "Výška",
            accessor: "height",
          },
          {
            Header: "BMI",
            accessor: "bmi",
          },
          {
            Header: "Rodné číslo",
            accessor: "birth_number",
          },
          {
            Header: "ID pacienta",
            accessor: "external_id",
          },
          {
            Header: "Dnů hospitalizace (k dnešnímu dni)",
            accessor: "days",
          },
          {
            Header: "Datum posledního dekurzu",
            accessor: "last_dekurz",
          },
          {
            Header: "Ošetřující lékař",
            accessor: "doctor",
          },
          {
            Header: "Klinika",
            accessor: "clinic",
          },
          {
            Header: "Oddělení",
            accessor: "department",
          },
        ],
      },
    ],
    []
  );

  // Načítání laboratórních dat
  useEffect(() => {
    const fetchSamplings = async () => {
      setLoading(true);
      try {
        if (USE_MOCK_DATA) {
          // Použitie mock dát
          let filteredSamplings = [...mockSamplings];

          // Aplikovanie filtrov na mock dáta
          const filterParams = getFilterParams();

          if (filterParams.made_at__date) {
            // Filter pre aktuálny deň
            const targetDate = filterParams.made_at__date;
            filteredSamplings = filteredSamplings.filter((sampling) =>
              sampling.made_at.startsWith(targetDate)
            );
          } else if (filterParams.limit) {
            // Filter pre posledných X záznamov
            filteredSamplings = filteredSamplings.slice(0, filterParams.limit);
          } else if (filterParams.made_at__gte || filterParams.made_at__lte) {
            // Filter pre vlastné dátumové rozpätie
            filteredSamplings = filteredSamplings.filter((sampling) => {
              const samplingDate = new Date(sampling.made_at);
              let isInRange = true;

              if (filterParams.made_at__gte) {
                isInRange =
                  isInRange &&
                  samplingDate >= new Date(filterParams.made_at__gte);
              }
              if (filterParams.made_at__lte) {
                isInRange =
                  isInRange &&
                  samplingDate <= new Date(filterParams.made_at__lte);
              }

              return isInRange;
            });
          }

          setSamplings(filteredSamplings);
          setError(null);
        } else {
          // Pôvodné API volanie
          const params = {
            patient: patient?.id,
            care: care.id,
            ...getFilterParams(),
          };

          const response = await laboratoryService.getSamplings(params);
          setSamplings(response.results || []);
          setError(null);
        }
      } catch (err) {
        setError(err.message || "Chyba při načítání dat");
        setSamplings([]);
      } finally {
        setLoading(false);
      }
    };

    if (patient?.id || USE_MOCK_DATA) {
      fetchSamplings();
    }
  }, [patient?.id, filterOptions, dateRange]);

  const getFilterParams = () => {
    const today = new Date();
    const params = {};

    switch (filterOptions) {
      case "current":
        params.made_at__date = today.toISOString().split("T")[0];
        break;
      case "last100":
        params.limit = 100;
        break;
      case "all":
        // Žádné omezení
        break;
      case "custom":
        if (dateRange.from) {
          params.made_at__gte = dateRange.from;
        }
        if (dateRange.to) {
          params.made_at__lte = dateRange.to;
        }
        break;
      default:
        break;
    }

    return params;
  };

  const handleFilterChange = (newFilter) => {
    setFilterOptions(newFilter);
  };

  const handleDateRangeChange = (range) => {
    setDateRange(range);
  };

  const handleSamplingSelect = (sampling) => {
    setSelectedSampling(sampling);
  };

  // Helper funkcia pre získanie hodnoty podľa result_type
  const getResultValue = (result) => {
    switch (result.result_type) {
      case "QUANTITATIVE":
        return result.value || "";
      case "QUALITATIVE":
        return result.text_value || "";
      case "TEXT":
        return result.rich_text_value?.replace(/<[^>]*>/g, "") || "";
      case "COMMENT":
        return result.comment_value || "";
      default:
        return result.value || result.text_value || "";
    }
  };

  const copyToClipboard = async (data) => {
    try {
      let textToCopy = "";

      if (Array.isArray(data)) {
        // Kopírování více výsledků
        textToCopy = data
          .map((item) => {
            const time = new Date(item.made_at).toLocaleString("cs-CZ");
            const value = getResultValue(item);
            return `${item.parameter_name}: ${value} ${
              item.parameter_units || ""
            } (${time})`;
          })
          .join("\n");
      } else if (data.parameter_results) {
        // Kopírování celého záznamu
        const time = new Date(data.made_at).toLocaleString("cs-CZ");
        textToCopy = `Laboratórní výsledky - ${time}\n`;
        textToCopy += `ID vzorku: ${data.external_id}\n\n`;

        data.parameter_results.forEach((result) => {
          const value = getResultValue(result);
          textToCopy += `${result.parameter_name}: ${value} ${
            result.parameter_units || ""
          }`;
          if (result.is_out_of_range) {
            textToCopy += " (mimo normu)";
          }
          textToCopy += "\n";
        });
      } else {
        // Kopírování jednotlivého výsledku
        const time = new Date(data.made_at).toLocaleString("cs-CZ");
        const value = getResultValue(data);
        textToCopy = `${data.parameter_name}: ${value} ${
          data.parameter_units || ""
        } (${time})`;
      }

      // Pokus o moderní clipboard API
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(textToCopy);
      } else {
        // Fallback pre staršie prehliadače alebo HTTP
        const textArea = document.createElement("textarea");
        textArea.value = textToCopy;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }

      // Zde by mohla být notifikace o úspěšnom kopírování
      console.log("Text bol úspešne skopírovaný do schránky");
    } catch (err) {
      console.error("Chyba při kopírování do schránky:", err);
      // Fallback - zobrazenie textu v alert dialógu
      alert("Kopírovanie do schránky zlyhalo. Text:\n\n" + textToCopy);
    }
  };

  return {
    samplings,
    selectedSampling,
    filterOptions,
    dateRange,
    loading,
    error,
    patientData,
    table,
    columns,
    patientRiskLevel,
    handleFilterChange,
    handleDateRangeChange,
    handleSamplingSelect,
    copyToClipboard,
  };
};
